import { UserCircle } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface MemberNameCellProps {
  name: string;
  subtitle?: string;
  className?: string;
  iconClassName?: string;
  size?: "sm" | "md";
  iconStyle?: "user" | "avatar";
}

/**
 * Consistent member/person name display with UserCircle icon.
 * Use wherever a member or person name appears in tables, lists, badges, etc.
 */
export default function MemberNameCell({
  name,
  subtitle,
  className,
  iconClassName,
  size = "md",
  iconStyle = "user",
}: MemberNameCellProps) {
  const iconSize = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
  const textSize = size === "sm" ? "text-xs" : "text-sm";
  const avatarSize = size === "sm" ? "w-6 h-6" : "w-7 h-7";
  const initials = name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .join("")
    .slice(0, 2);

  return (
    <span className={cn("inline-flex items-center gap-1.5", iconStyle === "avatar" && "gap-2", className)}>
      {iconStyle === "avatar" ? (
        <Avatar className={cn(avatarSize, "flex-shrink-0", iconClassName)}>
          <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
            {initials || "--"}
          </AvatarFallback>
        </Avatar>
      ) : (
        <UserCircle className={cn(iconSize, "text-muted-foreground flex-shrink-0", iconClassName)} />
      )}
      <span className="min-w-0">
        <span className={cn(textSize, "font-medium text-foreground")}>{name}</span>
        {subtitle && (
          <span className={cn("block text-xs text-muted-foreground leading-tight")}>{subtitle}</span>
        )}
      </span>
    </span>
  );
}
